import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close,
  CloudUploadOutlined,
  DeleteOutline,
  InsertDriveFileOutlined,
  Groups,
  Search,
  Send,
} from '@mui/icons-material';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDialogStyles from '../../hooks/useDialogStyles';
import { avatarColor, avatarSrc, getInitials } from '../../utils/avatar';
import { formatSize } from './MessageContent';

const MAX_FILES = 10;
const fileKey = (f) => `${f.name}-${f.size}-${f.lastModified}`;
const IMAGE_EXT = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp' };

// Browsers call every pasted screenshot "image.png": give each a unique name
const renamePasted = (files) =>
  files.map((f, i) =>
    f.type.startsWith('image/') && (!f.name || f.name === 'image.png')
      ? new File([f], `pasted-image-${Date.now()}-${i}.${IMAGE_EXT[f.type] || 'png'}`, { type: f.type, lastModified: Date.now() })
      : f
  );

// Small preview: a thumbnail for images, a file icon otherwise
const FileThumb = ({ file }) => {
  const theme = useTheme();
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!file.type.startsWith('image/')) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 1.5,
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
        color: theme.palette.secondary.main,
        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
        backgroundImage: url ? `url("${url}")` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {!url && <InsertDriveFileOutlined fontSize="small" />}
    </Box>
  );
};

/**
 * "Send files" dialog: pick / drop / paste files, add a title and a message,
 * choose the people and groups, then send. Each file becomes one message per
 * recipient; the title and message travel with the first file.
 *
 * users:          people that can receive (no bot, not me)
 * groups:         my groups
 * defaultTargets: ids selected when it opens (the open conversation)
 * onSent:         called with every created message
 */
const SendFilesDialog = ({ open, onClose, users = [], groups = [], defaultTargets = [], onSent }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const axiosPrivate = useAxiosPrivate();
  const { paperSx, titleSx, actionsSx, fieldSx, goldButtonSx, cancelButtonSx, scrollbarSx } = useDialogStyles();

  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targets, setTargets] = useState([]);
  const [query, setQuery] = useState('');
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);     // 0..100 over all uploads
  const [error, setError] = useState(null);

  const inputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const abortRef = useRef(null);

  // Fresh form every time it opens
  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setTitle('');
    setBody('');
    setTargets(defaultTargets);
    setQuery('');
    setError(null);
    setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFiles = (incoming) => {
    const picked = Array.from(incoming || []).filter((f) => f.size > 0);   // skips dropped folders
    if (picked.length === 0) return;
    setFiles((prev) => {
      const existing = new Set(prev.map(fileKey));
      const merged = [...prev, ...picked.filter((f) => !existing.has(fileKey(f)))];
      setError(merged.length > MAX_FILES ? `You can attach up to ${MAX_FILES} files at once.` : null);
      return merged.slice(0, MAX_FILES);
    });
  };

  // Ctrl+V anywhere in the dialog. Normal text pastes into the fields are left alone.
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const pasted = Array.from(e.clipboardData?.files || []);
      if (pasted.length === 0 || e.clipboardData.getData('text') || sending) return;
      e.preventDefault();
      addFiles(renamePasted(pasted));
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, sending]);

  // ---------- drag and drop ----------
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const dropHandlers = {
    onDragEnter: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepthRef.current += 1; setDragging(true); },
    onDragOver: (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; },
    onDragLeave: (e) => {
      if (!hasFiles(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragging(false);
    },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setDragging(false);
      if (!sending) addFiles(e.dataTransfer.files);
    },
  };

  // ---------- recipients ----------
  const recipients = useMemo(() => [
    ...groups.map((g) => ({ id: g._id, kind: 'group', name: g.name, avatar: g.avatar })),
    ...users.map((u) => ({ id: u._id, kind: 'user', name: u.name, avatar: u.avatar, isOnline: u.isOnline })),
  ], [users, groups]);

  const byId = useMemo(() => Object.fromEntries(recipients.map((r) => [r.id, r])), [recipients]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? recipients.filter((r) => r.name?.toLowerCase().includes(q)) : recipients;
  }, [recipients, query]);

  const toggleTarget = (id) =>
    setTargets((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // ---------- send ----------
  const content = [title.trim(), body.trim()].filter(Boolean).join('\n\n');
  const canSend = !sending && targets.length > 0 && (files.length > 0 || content);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    setProgress(0);

    const controller = new AbortController();
    abortRef.current = controller;

    // one message per file; text-only when no file
    const queue = files.length > 0 ? files : [null];
    const perTarget = queue.reduce((sum, f) => sum + (f ? f.size : 1), 0);
    const total = perTarget * targets.length || 1;
    let doneBytes = 0;
    const created = [];
    const failed = [];

    for (const id of targets) {
      const target = byId[id];
      if (!target) continue;
      try {
        for (let i = 0; i < queue.length; i++) {
          const file = queue[i];
          const form = new FormData();
          if (target.kind === 'group') form.append('groupId', id);
          else form.append('recipientId', id);
          form.append('content', i === 0 ? content : '');
          if (file) form.append('file', file);

          const size = file ? file.size : 1;
          const before = doneBytes;   // fixed copy for the progress callback
          const { data } = await axiosPrivate.post('/api/messages', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
            onUploadProgress: (e) => {
              if (!e.total) return;
              const part = Math.min(1, e.loaded / e.total) * size;
              setProgress(Math.min(99, Math.round(((before + part) * 100) / total)));
            },
          });
          doneBytes += size;
          setProgress(Math.min(99, Math.round((doneBytes * 100) / total)));
          created.push(data);
        }
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') break;
        console.error('Unable to send files', err);
        failed.push(id);
      }
    }

    abortRef.current = null;
    setSending(false);
    if (created.length) onSent?.(created);

    if (controller.signal.aborted) {
      setError('Sending was cancelled. Anything already delivered stays in the chat.');
    } else if (failed.length) {
      setError(`Could not send to ${failed.map((id) => byId[id]?.name).join(', ')}. Please try again.`);
      // keep only the ones that failed so a retry does not send twice
      setTargets(failed);
    } else {
      setProgress(100);
      onClose();
    }
  };

  const handleClose = () => {
    if (sending) {
      abortRef.current?.abort();
      return;
    }
    onClose();
  };

  const subtle = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const line = isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      PaperProps={{ sx: paperSx }}
    >
      <DialogTitle sx={{ ...titleSx, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            color: accent,
            backgroundColor: alpha(accent, 0.12),
            border: `1px solid ${alpha(accent, 0.3)}`,
          }}
        >
          <CloudUploadOutlined fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Send files</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>
            Attach documents, add a title and a message, then choose who gets them
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={handleClose} sx={{ color: theme.palette.text.secondary }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' },
          // fixed height on desktop: both columns fill it, the lists scroll inside
          height: { md: 480 },
          overflowY: { xs: 'auto', md: 'hidden' },
          backgroundColor: theme.palette.background.alt,
          ...scrollbarSx,
        }}
      >
        {/* ---------- left: files and text ---------- */}
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0, minHeight: 0, overflowY: { md: 'auto' }, ...scrollbarSx }}>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />

          {/* drop zone */}
          <Box
            role="button"
            tabIndex={0}
            onClick={() => !sending && inputRef.current?.click()}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !sending) { e.preventDefault(); inputRef.current?.click(); } }}
            {...dropHandlers}
            sx={{
              px: 2,
              py: files.length ? 1.5 : 2.75,
              flexShrink: 0,
              borderRadius: 2,
              textAlign: 'center',
              cursor: sending ? 'default' : 'pointer',
              outline: 'none',
              border: `1.5px dashed ${dragging ? accent : alpha(accent, 0.4)}`,
              backgroundColor: dragging ? alpha(accent, 0.1) : subtle,
              transition: 'background-color .15s ease, border-color .15s ease',
              '&:hover, &:focus-visible': { borderColor: accent, backgroundColor: alpha(accent, 0.06) },
            }}
          >
            <CloudUploadOutlined sx={{ fontSize: files.length ? 26 : 34, color: accent, mb: 0.5 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
              {dragging ? 'Drop the files here' : <>Drag &amp; drop files, or <Box component="span" sx={{ color: accent }}>browse</Box></>}
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', color: theme.palette.text.secondary, mt: 0.25 }}>
              You can also paste screenshots with Ctrl+V · up to {MAX_FILES} files
            </Typography>
          </Box>

          {/* file list */}
          {files.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0, maxHeight: 156, overflowY: 'auto', pr: 0.5, ...scrollbarSx }}>
              {files.map((f) => (
                <Box
                  key={fileKey(f)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 0.75,
                    pr: 0.5,
                    borderRadius: 1.5,
                    border: `1px solid ${line}`,
                    backgroundColor: subtle,
                  }}
                >
                  <FileThumb file={f} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{f.name}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}>{formatSize(f.size)}</Typography>
                  </Box>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        disabled={sending}
                        onClick={() => setFiles((prev) => prev.filter((x) => x !== f))}
                        sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.error.main } }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}

          <TextField
            label="Title"
            placeholder="e.g. Weekly radar report"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={sending}
            inputProps={{ maxLength: 120 }}
            size="small"
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Message"
            size="small"
            placeholder="Describe the attached documents…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sending}
            multiline
            minRows={4}
            fullWidth
            sx={{
              ...fieldSx,
              // on desktop the message box takes the rest of the column
              flex: { md: 1 },
              minHeight: { md: 112 },
              '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: { md: '100%' }, alignItems: 'flex-start' },
              '& textarea': { height: { md: '100% !important' }, overflowY: 'auto !important', ...scrollbarSx },
            }}
          />
        </Box>

        {/* ---------- right: recipients ---------- */}
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            minWidth: 0,
            minHeight: 0,
            borderLeft: { md: `1px solid ${theme.palette.divider}` },
            borderTop: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
            backgroundColor: subtle,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: accent }}>
              Send to
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
              {targets.length} selected
              {targets.length > 0 && !sending && (
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={() => setTargets([])}
                  onKeyDown={(e) => { if (e.key === 'Enter') setTargets([]); }}
                  sx={{ ml: 1, color: accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  Clear
                </Box>
              )}
            </Typography>
          </Box>

          {/* selected chips */}
          {targets.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 76, overflowY: 'auto', ...scrollbarSx }}>
              {targets.map((id) => byId[id] && (
                <Chip
                  key={id}
                  size="small"
                  label={byId[id].name}
                  icon={byId[id].kind === 'group' ? <Groups /> : undefined}
                  onDelete={sending ? undefined : () => toggleTarget(id)}
                  sx={{
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(accent, 0.14),
                    border: `1px solid ${alpha(accent, 0.35)}`,
                    '& .MuiChip-icon': { color: accent },
                    '& .MuiChip-deleteIcon': { color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } },
                  }}
                />
              ))}
            </Box>
          )}

          <TextField
            placeholder="Search people and groups"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            fullWidth
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setQuery('')}>
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 180, md: 0 },
              maxHeight: { xs: 260, md: 'none' },
              overflowY: 'auto',
              mx: -1,
              ...scrollbarSx,
            }}
          >
            {visible.length === 0 && (
              <Typography sx={{ px: 1, py: 2, fontSize: '0.85rem', color: theme.palette.text.secondary, textAlign: 'center' }}>
                Nobody matches “{query}”
              </Typography>
            )}
            {visible.map((r) => {
              const checked = targets.includes(r.id);
              return (
                <Box
                  key={r.id}
                  role="checkbox"
                  aria-checked={checked}
                  tabIndex={0}
                  onClick={() => !sending && toggleTarget(r.id)}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !sending) { e.preventDefault(); toggleTarget(r.id); } }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1,
                    py: 0.45,
                    borderRadius: 1.5,
                    cursor: sending ? 'default' : 'pointer',
                    outline: 'none',
                    backgroundColor: checked ? alpha(accent, 0.1) : 'transparent',
                    '&:hover, &:focus-visible': { backgroundColor: checked ? alpha(accent, 0.16) : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)') },
                  }}
                >
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar
                      src={avatarSrc(r.avatar)}
                      sx={{ width: 30, height: 30, fontSize: '0.74rem', ...avatarColor(r.name, theme.palette.mode) }}
                    >
                      {r.kind === 'group' ? <Groups fontSize="small" /> : getInitials(r.name)}
                    </Avatar>
                    {r.kind === 'user' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: -1,
                          bottom: -1,
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          backgroundColor: r.isOnline ? '#35C759' : '#9E9E9E',
                          border: `2px solid ${theme.palette.background.alt}`,
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.84rem', lineHeight: 1.3, fontWeight: checked ? 700 : 500 }}>{r.name}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}>
                      {r.kind === 'group' ? 'Group' : r.isOnline ? 'Online' : 'Offline'}
                    </Typography>
                  </Box>
                  <Checkbox
                    checked={checked}
                    tabIndex={-1}
                    size="small"
                    disableRipple
                    sx={{ p: 0.5, color: theme.palette.text.secondary, '&.Mui-checked': { color: accent } }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      {sending && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 3, backgroundColor: alpha(accent, 0.12), '& .MuiLinearProgress-bar': { backgroundColor: accent } }}
        />
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      <DialogActions sx={{ ...actionsSx, justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>
          {sending
            ? `Sending… ${progress}%`
            : `${files.length} file${files.length === 1 ? '' : 's'} · ${targets.length} recipient${targets.length === 1 ? '' : 's'}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} sx={cancelButtonSx}>
            {sending ? 'Stop' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            disabled={!canSend}
            onClick={handleSend}
            sx={{
              ...goldButtonSx,
              '&.Mui-disabled': { backgroundColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)', color: theme.palette.text.disabled },
            }}
          >
            Send
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SendFilesDialog;
